/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import { STUDENT_IMAGE_UPLOAD_ACCEPT } from "@core/shared/utils/uploaded-file-types";
import { trans } from "@mongez/localization";
import {
  FormControlProps,
  requiredRule,
  useFormControl,
} from "@mongez/react-form";
import { Image as ImageIcon, Upload, X } from "lucide-react";
import { ReactNode } from "react";
import { useResourceImage } from "../../hooks/use-resource-image";
import { cn } from "../../lib/utils";
import { InputError } from "./InputError";
import { InputLabel } from "./InputLabel";
import { InputDescription } from "./Utils";

type ResourceImageInputProps = FormControlProps & {
  label: ReactNode;
  description?: ReactNode;
  size?: "full" | "compact";
};

export function ResourceImageInput(props: ResourceImageInputProps) {
  const { size = "full", ...restProps } = props;
  const { error, changeValue } = useFormControl({
    rules: [requiredRule],
    ...restProps,
  });

  const {
    displayedImage,
    isDragging,
    isUploading,
    fileInputRef,
    handleDrop,
    handleRemove,
    handleClick,
    handleDragOver,
    handleDragLeave,
    handleFileInput,
  } = useResourceImage({
    ...restProps,
    onChange: (resource) => changeValue(resource.id),
  });

  return (
    <div className="flex w-full flex-col gap-1.5">
      <div className="flex flex-col gap-1">
        {props.label && (
          <InputLabel
            htmlFor={props.id}
            className="text-center"
            required={props.required}
          >
            {props.label}
          </InputLabel>
        )}

        {props.description && (
          <InputDescription>{props.description}</InputDescription>
        )}
      </div>

      <div className="relative">
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept={STUDENT_IMAGE_UPLOAD_ACCEPT}
          onChange={handleFileInput}
          className="hidden"
        />

        {/* Upload area */}
        <div
          onClick={handleClick}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={cn(
            "relative cursor-pointer rounded-lg border-2 border-dashed transition-all duration-200",
            size === "compact" ? "p-4" : "p-6",
            "hover:border-primary/60 hover:bg-primary/5 dark:hover:bg-primary/10",
            "border-gray-300 dark:border-gray-600",
            isDragging && "border-primary bg-primary/10 dark:bg-primary/20",
            displayedImage && "border-solid border-gray-200 dark:border-gray-600",
            error && "border-red-500 dark:border-red-400",
          )}
        >
          {displayedImage ? (
            // Image preview mode
            <div className="group relative">
              <img
                src={displayedImage}
                alt="Resource"
                className={cn(
                  "w-full rounded-md object-cover",
                  size === "compact" ? "h-32" : "h-48",
                )}
              />

              {/* Overlay with remove button */}
              <div className="absolute inset-0 flex items-center justify-center rounded-md bg-black/0 transition-all duration-200 group-hover:bg-black/20">
                <button
                  type="button"
                  onClick={handleRemove}
                  className="rounded-full bg-red-500 p-2 text-white opacity-0 transition-all duration-200 hover:bg-red-600 group-hover:opacity-100"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Uploading overlay */}
              {isUploading && (
                <div className="absolute inset-0 flex items-center justify-center rounded-md bg-black/50">
                  <div className="flex flex-col items-center gap-2 text-white">
                    <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-white"></div>
                    <span className="text-sm">{trans("uploading")}</span>
                  </div>
                </div>
              )}
            </div>
          ) : (
            // Empty state
            <div className="flex flex-col items-center justify-center gap-4 text-center">
              {isUploading ? (
                <div className="flex flex-col items-center gap-2">
                  <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary"></div>
                  <span className="text-sm text-gray-600 dark:text-gray-300">
                    {trans("uploading")}
                  </span>
                </div>
              ) : (
                <>
                  <div
                    className={cn(
                      "rounded-full bg-primary/10",
                      size === "compact" ? "p-2" : "p-3",
                    )}
                  >
                    <ImageIcon
                      className={cn(
                        "text-primary",
                        size === "compact" ? "h-5 w-5" : "h-6 w-6",
                      )}
                    />
                  </div>
                  <div className="space-y-2">
                    <p
                      className={cn(
                        "font-medium text-gray-900 dark:text-gray-100",
                        size === "compact" ? "text-xs" : "text-sm",
                      )}
                    >
                      {trans("clickToUploadOrDragAndDrop")}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {trans("imageFileTypes")}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                    <Upload className="h-4 w-4" />
                    <span>{trans("browseFiles")}</span>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <InputError error={error} />
    </div>
  );
}
